import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { ImageEntity } from '../../types';
import styles from './Gallery.module.scss';
import Slider from 'react-slick';
import classnames from 'classnames';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { FormattedMessage, useIntl } from 'react-intl';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { withBreakpoints } from 'react-breakpoints';
import { LinkWithIcon } from 'src/modules/products/components/downloads/LinkWithIcon/LinkWithIcon';
import ScrollBound from 'react-scroll-bound';
import { Drawer } from 'antd';
import Link from 'next/link';
import { ProductLocalizedEntity } from 'src/modules/products';
import { trim } from 'lodash';

export interface ImageComponentEntity {
  image: ImageEntity;
  caption: string;
  products: ProductLocalizedEntity[];
}

export interface GalleryProps {
  images: ImageComponentEntity[];
  currentBreakpoint: string;
  breakpoints: { [currentBreakpoint: string]: number };
}

const speed = 500;
const desktopSlideHeight = 528; // 33 baselines

const Gallery: React.FunctionComponent<GalleryProps> = (props: GalleryProps) => {
  const { images: rawImages, currentBreakpoint, breakpoints } = props;

  const images = rawImages.filter((i) => !!i && !!i.image);

  const intl = useIntl();
  const slider = useRef<Slider>();
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const [activeImage, setActiveImage] = useState<ImageComponentEntity>(images[activeImageIndex]);
  const [isGalleryOpen, setIsGalleryOpen] = useState<boolean>(false);
  const [hack, setHack] = useState<string>('');

  useEffect(() => {
    setHack('hack');
  }, []);

  const slideLeft = useCallback(() => {
    slider.current.slickPrev();
  }, []);

  const slideRight = useCallback(() => {
    slider.current.slickNext();
  }, []);

  const openGallery = useCallback(() => {
    setIsGalleryOpen(true);
  }, []);

  const closeGallery = useCallback(() => {
    setIsGalleryOpen(false);
  }, []);

  const isSmallScreen = useMemo(() => breakpoints[currentBreakpoint] < breakpoints.md, [
    breakpoints,
    currentBreakpoint,
  ]);

  if (!images.length || !currentBreakpoint) {
    return <div />;
  }

  return (
    <React.Fragment key={`Gallery-${hack}-${currentBreakpoint}`}>
      <div className={styles.container}>
        <Slider
          ref={slider}
          dots={false}
          arrows={false}
          centerMode={false}
          slidesToShow={1}
          slidesToScroll={1}
          variableWidth={!isSmallScreen}
          infinite={true}
          adaptiveHeight={true}
          speed={speed}
          afterChange={(newIndex) => {
            setActiveImage(images[newIndex]);
            setActiveImageIndex(newIndex);
          }}
          className={styles.sliderWrapper}>
          {images.map((image, idx) => {
            let slideSizeStyle = {};

            // don't generate styles for server-side rendering
            if (currentBreakpoint) {
              slideSizeStyle = isSmallScreen
                ? {
                    width: '100%',
                  }
                : {
                    width: `${Math.round(
                      (image?.image?.width * desktopSlideHeight) / image?.image?.height
                    )}px`,
                    height: `${desktopSlideHeight}px`,
                  };
            }

            return (
              <div
                key={`${image.image?.id || idx}-${currentBreakpoint}-${
                  isSmallScreen ? 'small' : 'large'
                }`}
                data-index={idx}
                className={classnames([
                  styles.slideWrapper,
                  image.image.id === activeImage?.image.id ? styles.active : null,
                ])}>
                <img src={image.image.url} style={slideSizeStyle} />
              </div>
            );
          })}
        </Slider>
        <div className={styles.footer}>
          <div className={styles.largeScreenControls}>
            <TransitionGroup
              className={classnames(styles.captionContainer, styles.footerTextContainer)}
              transitionEnterTimeout={300}
              transitionLeaveTimeout={150}>
              <CSSTransition
                key={`caption-transition-container-${activeImage.image.id}`}
                timeout={300}>
                {
                  <div className={styles.animatedFooterText}>
                    <span
                      className={styles.caption}
                      dangerouslySetInnerHTML={{ __html: activeImage.caption }}
                    />
                    {activeImage.products?.map((product, idx) => (
                      <>
                        <Link
                          href={`/products/${
                            product.product_categories?.length
                              ? product.product_categories[0]?.slug
                              : 'category'
                          }/${product.slug}`}
                          as={`/products/${
                            product.product_categories?.length
                              ? product.product_categories[0]?.slug
                              : 'category'
                          }/${product.slug}`}>
                          <a className={styles.productLink}>{`${trim(product.name)}${
                            idx !== activeImage.products.length - 1 ? ', ' : ''
                          }`}</a>
                        </Link>
                      </>
                    ))}
                  </div>
                }
              </CSSTransition>
            </TransitionGroup>
          </div>

          <div className={classnames(styles.controlsContainer, styles.largeScreenControls)}>
            <GlobalIcon
              image={IconImage.LeftArrow}
              className={classnames(styles.arrow, styles.leftArrow)}
              size="custom"
              onClick={slideLeft}
            />
            <TransitionGroup
              className={classnames(styles.footerTextContainer)}
              transitionEnterTimeout={300}
              transitionLeaveTimeout={150}>
              <CSSTransition key={`current-image-number-${activeImage.image.id}`} timeout={300}>
                <div aria-hidden className={styles.animatedFooterText}>
                  {activeImageIndex + 1}
                </div>
              </CSSTransition>
            </TransitionGroup>{' '}
            <span className={styles.footerTextContainer}>
              {intl.formatMessage({ id: 'gallery/current_image_number_of_total_images_length' })}
            </span>{' '}
            <span className={styles.footerTextContainer}>{images.length}</span>
            <GlobalIcon
              image={IconImage.RightArrow}
              className={classnames(styles.arrow, styles.rightArrow)}
              size="custom"
              onClick={slideRight}
            />
          </div>

          <div className={styles.smallScreenControls}>
            <LinkWithIcon onClick={openGallery} icon={<GlobalIcon image={IconImage.Gallery} />}>
              Open Gallery
            </LinkWithIcon>
          </div>
        </div>
      </div>
      <Drawer
        className={styles.drawer}
        width="100%"
        placement="right"
        visible={isGalleryOpen}
        closable={false}
        headerStyle={{ height: 0, overflow: 'hidden' }}>
        <div className={styles.drawerInnerContainer}>
          <div className={styles.titleBar}>
            <div className={styles.titleLabel}>
              <FormattedMessage id="gallery/title" />
            </div>
            <div className={styles.closeDrawerButton} onClick={closeGallery}>
              <GlobalIcon
                image={IconImage.Close}
                size="custom"
                className={styles.closeDrawerIcon}
              />
            </div>
          </div>
          <ScrollBound className={styles.drawerContentContainer} tagName="div">
            {images.map((image) => (
              <div
                className={styles.drawerImageContainer}
                key={`drawer-image-container-${image.image.id}`}>
                <img src={image.image.url} className={styles.drawerImage} />
                <div
                  className={styles.drawerImageCaption}
                  dangerouslySetInnerHTML={{ __html: image.caption }}
                />
              </div>
            ))}
          </ScrollBound>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default withBreakpoints(Gallery);
